
import { createClient, SupabaseClient } from '@supabase/supabase-js';

/**
 * Raizama Garden CARE - Supabase Connection
 * 
 * Implementa inicialização preguiçosa (lazy) para evitar crash caso as variáveis
 * de ambiente não estejam configuradas no momento do carregamento do módulo.
 */

// @ts-ignore - Suporte a variáveis de ambiente do Vite
const env = import.meta.env || {};

const getEnvVar = (viteKey: string, fallbackKey: string): string => {
  try {
    // @ts-ignore
    const viteVal = import.meta.env ? import.meta.env[viteKey] : undefined;
    if (viteVal && viteVal !== 'undefined' && viteVal !== 'null' && viteVal !== '') {
      return viteVal;
    }
  } catch (e) {
    // Ignore error
  }

  try {
    if (typeof process !== 'undefined' && process.env) {
      const processVal = process.env[fallbackKey] || process.env[viteKey];
      if (processVal && processVal !== 'undefined' && processVal !== 'null' && processVal !== '') {
        return processVal;
      }
    }
  } catch (e) {
    // Ignore error
  }

  return '';
};

let rawUrl = getEnvVar('VITE_SUPABASE_URL', 'SUPABASE_URL');

// Se o usuário configurar apenas o ID do projeto (ex: "dvncpslpjsggfknqxuga"), expandimos para a URL completa do Supabase
if (rawUrl && !rawUrl.startsWith('http://') && !rawUrl.startsWith('https://')) {
  if (!rawUrl.includes('.') && !rawUrl.includes('/')) {
    rawUrl = `https://${rawUrl.trim()}.supabase.co`;
  } else {
    rawUrl = `https://${rawUrl.trim()}`;
  }
}

const supabaseUrl = rawUrl || 'https://dvncpslpjsggfknqxuga.supabase.co';
const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY', 'SUPABASE_ANON_KEY') || 
                       getEnvVar('VITE_SUPABASE_KEY', 'SUPABASE_KEY') ||
                       getEnvVar('SUPABASE_KEY', 'SUPABASE_KEY') ||
                       'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR2bmNwc2xwanNnZ2ZrbnF4dWdhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc0MTIyNDQsImV4cCI6MjA4Mjk4ODI0NH0.3vMPnI9XUUyPouzuaslKbG6l1UXyEXVnJ0fne38JEyc';

let instance: SupabaseClient | null = null;

export const getSupabase = (): SupabaseClient => {
  if (!instance) {
    const isValidUrl = supabaseUrl && (supabaseUrl.startsWith('http://') || supabaseUrl.startsWith('https://'));

    if (!supabaseUrl || !supabaseAnonKey || !isValidUrl) {
      console.error("❌ [Supabase Config Error]: URL ou chave secreta do Supabase ausentes ou inválidas.");
      console.error(`URL configurada: "${supabaseUrl}"`);
      console.error(`Chave configurada: ${supabaseAnonKey ? 'Presente' : 'Ausente'}`);

      throw new Error(
        "Configuração do Supabase inválida ou ausente. " +
        "Certifique-se de que a variável VITE_SUPABASE_URL começa com http/https " +
        "e que a variável VITE_SUPABASE_ANON_KEY esteja preenchida corretamente no seu arquivo .env."
      );
    }
    instance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    });
  }
  return instance;
};

// Proxy para manter a compatibilidade com as importações existentes: import { supabase } from '...'
export const supabase = new Proxy({} as SupabaseClient, {
  get: (_target, prop) => {
    const client = getSupabase();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  }
});

export const getSubscription = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching subscription:', error);
      return null;
    }
    return data;
  } catch (err) {
    console.error('Error in getSubscription:', err);
    return null;
  }
};
